import type { QueuedOperation, QueueStorage, RejectedOperation } from "./queue.ts";

/**
 * IndexedDB, for the Capacitor shell.
 *
 * Written against the platform API rather than a wrapper library — it is sixty
 * lines, and `13-house-style.md` puts a new dependency last. Kept out of the
 * package's main entry so a server-side consumer never pulls DOM types in
 * behind it.
 */

const OUTBOUND = "outbound";
const REJECTED = "rejected";

/**
 * Bumped from 1 when the dead-letter store arrived. A till upgrading in place
 * keeps whatever was still queued: `onupgradeneeded` only adds the store it
 * finds missing, and the outbound one is left exactly as it was.
 */
const VERSION = 2;

/**
 * Every write is its own transaction on purpose: a till that crashes
 * mid-service must not lose the operation it was in the middle of queueing,
 * and a transaction that spanned the drain would hold a lock across the
 * network.
 */
export function indexedDbQueueStorage(
  factory: IDBFactory,
  databaseName = "tovi-pos",
): QueueStorage {
  let opened: Promise<IDBDatabase> | null = null;

  function open(): Promise<IDBDatabase> {
    opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = factory.open(databaseName, VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(OUTBOUND)) {
          database.createObjectStore(OUTBOUND, { keyPath: "id" });
        }
        // Keyed by the operation's id, so a refusal replayed after a crash
        // lands on the row it already has instead of a second one.
        if (!database.objectStoreNames.contains(REJECTED)) {
          database.createObjectStore(REJECTED, { keyPath: "operation.id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return opened;
  }

  async function transact<T>(
    store: string,
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const database = await open();
    return new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(store, mode);
      const request = run(transaction.objectStore(store));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  return {
    all: () =>
      transact(OUTBOUND, "readonly", (store) => store.getAll() as IDBRequest<QueuedOperation[]>),
    put: async (operation) => {
      await transact(
        OUTBOUND,
        "readwrite",
        (store) => store.put(operation) as IDBRequest<IDBValidKey>,
      );
    },
    remove: async (id) => {
      await transact(OUTBOUND, "readwrite", (store) => store.delete(id) as IDBRequest<undefined>);
    },
    reject: async (entry) => {
      await transact(REJECTED, "readwrite", (store) => store.put(entry) as IDBRequest<IDBValidKey>);
    },
    rejected: () =>
      transact(REJECTED, "readonly", (store) => store.getAll() as IDBRequest<RejectedOperation[]>),
    clearRejection: async (id) => {
      await transact(REJECTED, "readwrite", (store) => store.delete(id) as IDBRequest<undefined>);
    },
  };
}
